import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, Renderer2, ViewChild, ViewEncapsulation } from "@angular/core";
import type { EmojiButton } from "@joeattardi/emoji-button";
import { RichTextEditorIconEnum } from "@webapp/shared/rich-text-editor/rich-text-editor-icon/rich-text-editor-icon.enum";

type EmojiPickerMenuPosition = "top" | "middle" | "bottom";

@Component({
  selector: "emoji-picker",
  templateUrl: "./emoji-picker.component.html",
  styleUrls: ["./emoji-picker.component.less"],
  encapsulation: ViewEncapsulation.None,
})
export class EmojiPickerComponent implements AfterViewInit, OnDestroy {
  @Input() public outOfFocus: boolean;

  @Output() public readonly selectedEmoji = new EventEmitter<string>();
  @Output() public readonly pickerOpen = new EventEmitter<MouseEvent>();
  @Output() public readonly pickerClose = new EventEmitter<MouseEvent>();
  @Output() public readonly emojiPickerCreation = new EventEmitter();

  @ViewChild("emojiPickerContainer") public emojiPickerContainer: ElementRef;

  public emojiPicker: EmojiButton;
  public loadingEmojiPicker = false;

  public showEmojiPickerPolygon = false;

  public emojiPickerPosition: EmojiPickerMenuPosition;
  public staticMenuPosition: EmojiPickerMenuPosition;
  public emojiIcon = RichTextEditorIconEnum.EMOJI;

  private removeClickListener: () => void;

  constructor(private elementRef: ElementRef, private renderer: Renderer2, private ngZone: NgZone) {}

  public ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.removeClickListener = this.renderer.listen("document", "click", (event: PointerEvent) => {
        if (!this.elementRef.nativeElement.contains(event?.target) && this.showEmojiPickerPolygon) {
          this.closeEmojiPickerModal(event);
        }
      });
    });
  }

  public ngOnDestroy(): void {
    if (this.removeClickListener) {
      this.removeClickListener();
      this.removeClickListener = null;
    }

    if (this.emojiPicker) {
      this.emojiPicker.off("emoji", this.handleEmojiSelected);
      this.emojiPicker.destroyPicker();
      this.emojiPicker = null;
    }
  }

  public toggleEmojiPicker(event: MouseEvent): void {
    if (!this.emojiPicker && !this.loadingEmojiPicker) {
      this.createEmojiPicker().then(() => this.positionEmojiPicker(event));
    } else {
      this.positionEmojiPicker(event);
    }
  }

  public async createEmojiPicker(): Promise<void> {
    this.loadingEmojiPicker = true;
    try {
      // Load emoji-button as separate chunks
      const { EmojiButton } = await import("@joeattardi/emoji-button");

      this.emojiPickerCreation.emit();

      this.emojiPicker = new EmojiButton({
        rootElement: this.emojiPickerContainer?.nativeElement,
        showAnimation: false,
        autoFocusSearch: false,
      });
      this.emojiPicker.on("emoji", this.handleEmojiSelected);
    } catch (err) {
      console.error("Error loading emoji-button library", err);
    } finally {
      this.loadingEmojiPicker = false;
    }
  }

  private positionEmojiPicker(event: MouseEvent): void {
    if (this.emojiPicker) {
      this.emojiPicker.togglePicker(event.currentTarget as HTMLElement);
      this.emojiPickerPosition = this.staticMenuPosition || this.getMenuPosition();
      this.showEmojiPickerPolygon = !this.showEmojiPickerPolygon;
    }

    if (this.showEmojiPickerPolygon) {
      this.pickerOpen.emit(event);
    }
  }

  public closeEmojiPickerModal(event?: MouseEvent): void {
    this.emojiPicker.hidePicker();
    this.showEmojiPickerPolygon = false;

    this.pickerClose.emit(event);
  }

  private handleEmojiSelected = (event): void => {
    this.selectedEmoji.emit(event.emoji);
    this.closeEmojiPickerModal();
  };

  private getMenuPosition = (): EmojiPickerMenuPosition => {
    const addGifElemTopOffset = this.elementRef.nativeElement.getBoundingClientRect().top;
    const availableHeightBot = window.screen.availHeight - addGifElemTopOffset;

    if (availableHeightBot > 700) {
      return "top";
    } else if (availableHeightBot > 500) {
      return "middle";
    } else {
      return "bottom";
    }
  };
}
